/**
 * Quick Collection Filter
 *
 * Enables seamless switching between collections without a full page reload.
 * Fetches the target collection's banner and product grid via Shopify's
 * Section Rendering API (?section_id=), swaps the DOM content, and updates
 * the URL with pushState. Coordinates with facets.js to reset filter state.
 */
class CollectionQuickFilter extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll('.collection-quick-filter__tab');
    this.shopName = this.dataset.shopName || '';
    this.gridSectionId = document.getElementById('product-grid')?.dataset.id;
    this.bannerSectionId = this.getBannerSectionId();

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick.bind(this));
    });

    window.addEventListener('popstate', this.onPopState.bind(this));
  }

  // Reads the banner's section ID from the Shopify section wrapper element
  getBannerSectionId() {
    const banner = document.getElementById('CollectionBanner');
    if (!banner) return null;
    const wrapper = banner.closest('[id^="shopify-section-"]');
    if (!wrapper) return null;
    return wrapper.id.replace('shopify-section-', '');
  }

  onTabClick(event) {
    event.preventDefault();
    const targetUrl = event.currentTarget.dataset.collectionUrl;
    if (targetUrl === window.location.pathname) return;
    this.switchCollection(targetUrl, true);
  }

  // Fetches banner + product grid for the target collection, swaps DOM, and updates URL
  async switchCollection(targetUrl, pushState) {
    // Non-collection links fall back to normal navigation
    if (!targetUrl.startsWith('/collections/')) {
      window.location.href = targetUrl;
      return;
    }

    this.setActiveTab(targetUrl);
    this.showLoading();

    const [bannerHtml, gridHtml] = await Promise.all([
      this.fetchSection(targetUrl, this.bannerSectionId),
      this.fetchSection(targetUrl, this.gridSectionId),
    ]);

    if (bannerHtml) this.updateBanner(bannerHtml);
    if (gridHtml) this.updateProductGrid(gridHtml);
    this.updatePageTitle(bannerHtml);

    if (pushState) {
      history.pushState({ collectionQuickFilter: true, collectionUrl: targetUrl, searchParams: '' }, '', targetUrl);
    }

    this.resetFacets();
    this.hideLoading();

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(document.getElementById('ProductGridContainer'));
    }

    this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // Fetches a single section's HTML from a collection URL using the Section Rendering API
  async fetchSection(collectionUrl, sectionId) {
    if (!sectionId) return null;
    try {
      const response = await fetch(`${collectionUrl}?section_id=${sectionId}`);
      if (!response.ok) return null;
      return await response.text();
    } catch {
      return null;
    }
  }

  // Replaces the collection banner content (title, description, image)
  updateBanner(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newBanner = doc.querySelector('.collection-hero');
    const currentBanner = document.getElementById('CollectionBanner');
    if (newBanner && currentBanner) {
      currentBanner.className = newBanner.className;
      currentBanner.innerHTML = newBanner.innerHTML;
    }
  }

  // Replaces the product grid, facet filters, and product count
  updateProductGrid(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const newGrid = doc.getElementById('ProductGridContainer');
    const currentGrid = document.getElementById('ProductGridContainer');
    if (newGrid && currentGrid) {
      currentGrid.innerHTML = newGrid.innerHTML;
      currentGrid.querySelectorAll('.scroll-trigger').forEach((el) => {
        el.classList.add('scroll-trigger--cancel');
      });
    }

    const newFilters = doc.getElementById('main-collection-filters');
    const currentFilters = document.getElementById('main-collection-filters');
    if (newFilters && currentFilters) {
      currentFilters.innerHTML = newFilters.innerHTML;
    }

    const newCount = doc.getElementById('ProductCount');
    const currentCount = document.getElementById('ProductCount');
    if (newCount && currentCount) currentCount.innerHTML = newCount.innerHTML;

    const currentCountDesktop = document.getElementById('ProductCountDesktop');
    if (newCount && currentCountDesktop) currentCountDesktop.innerHTML = newCount.innerHTML;
  }

  // Updates the browser tab title to match the new collection
  updatePageTitle(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const titleEl = doc.querySelector('.collection-hero__title');
    if (!titleEl) return;
    const titleText = titleEl.textContent.trim().replace(/^.*:\s*/, '');
    document.title = this.shopName ? `${titleText} – ${this.shopName}` : titleText;
  }

  // Toggles the active class and aria-current attribute on tabs
  setActiveTab(targetUrl) {
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.collectionUrl === targetUrl;
      tab.classList.toggle('collection-quick-filter__tab--active', isActive);
      if (isActive) {
        tab.setAttribute('aria-current', 'page');
      } else {
        tab.removeAttribute('aria-current');
      }
    });
  }

  // Clears the facets.js cache and resets filter state for the new collection
  resetFacets() {
    if (typeof FacetFiltersForm !== 'undefined') {
      FacetFiltersForm.filterData = [];
      FacetFiltersForm.searchParamsInitial = '';
      FacetFiltersForm.searchParamsPrev = '';
    }
  }

  showLoading() {
    const collection = document.getElementById('ProductGridContainer')?.querySelector('.collection');
    if (collection) collection.classList.add('loading');
  }

  hideLoading() {
    const collection = document.getElementById('ProductGridContainer')?.querySelector('.collection');
    if (collection) collection.classList.remove('loading');
  }

  // Handles browser back/forward to restore the correct collection
  onPopState(event) {
    const state = event.state;
    if (state && state.collectionQuickFilter) {
      this.switchCollection(state.collectionUrl, false);
      return;
    }

    const currentPath = window.location.pathname;
    const hasTab = Array.from(this.tabs).some((tab) => tab.dataset.collectionUrl === currentPath);
    if (hasTab) {
      this.switchCollection(currentPath, false);
    }
  }
}

customElements.define('collection-quick-filter', CollectionQuickFilter);
